/**
 * Default PII scrubber. Invoice line-item descriptions, client emails,
 * and free-form notes all end up as context fields at some point; this
 * redacts any key whose name matches a sensitive pattern before the
 * record ever reaches a sink.
 *
 * The list is intentionally conservative. We optimise for "never leak a
 * user's email to Sentry by accident" over "preserve maximum
 * debuggability". Code that genuinely needs a raw field at
 * troubleshoot time can pass it in a key not on this list (e.g.
 * `debugEmailPrefix` instead of `email`).
 */

const REDACTED_KEYS: ReadonlySet<string> = new Set([
  'email',
  'emails',
  'password',
  'passwordHash',
  'token',
  'authorization',
  'accessToken',
  'refreshToken',
  'secret',
  'apiKey',
  'ssn',
  'creditCard',
  'cardNumber',
  'cvv',
  'pin',
  'phone',
  'phoneNumber',
  'address',
  'streetAddress',
  'firstName',
  'lastName',
  'fullName',
  'displayName',
  'taxId',
  'notes',
  'description',
]);

const MAX_DEPTH = 4;
const MAX_STRING_LENGTH = 512;

export function defaultScrub(
  context: Record<string, unknown>,
): Record<string, unknown> {
  return scrubObject(context, 0) as Record<string, unknown>;
}

function scrubValue(key: string | null, value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH) return '[Too deep]';
  if (key !== null && isSensitiveKey(key)) return redact(value);

  if (value === null || value === undefined) return value;
  const t = typeof value;
  if (t === 'string') return truncateString(value as string);
  if (t === 'number' || t === 'boolean' || t === 'bigint') return value;
  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(null, v, depth + 1));
  }
  if (t === 'object') {
    return scrubObject(value as Record<string, unknown>, depth + 1);
  }
  // Functions, symbols, etc. are dropped — they leak behaviour and don't
  // round-trip over JSON.
  return `[${t}]`;
}

function scrubObject(
  obj: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = scrubValue(k, v, depth);
  }
  return out;
}

function isSensitiveKey(key: string): boolean {
  if (REDACTED_KEYS.has(key)) return true;
  const lower = key.toLowerCase();
  // Catch variations like `userEmail`, `billing_email`, `ownerSsn`.
  for (const sensitive of REDACTED_KEYS) {
    const s = sensitive.toLowerCase();
    if (lower === s) return true;
    if (lower.endsWith(s)) return true;
  }
  return false;
}

function redact(value: unknown): string {
  if (value === null || value === undefined) return '[redacted]';
  if (typeof value === 'string') return `[redacted:${value.length}]`;
  return '[redacted]';
}

function truncateString(s: string): string {
  if (s.length <= MAX_STRING_LENGTH) return s;
  return `${s.slice(0, MAX_STRING_LENGTH)}…[${s.length - MAX_STRING_LENGTH} more]`;
}
